﻿<script setup lang="ts">
    import { inject, onMounted, reactive } from 'vue';
    import { refreshTranslations } from './BackOfficeRoot.access';
    import { useRouter } from 'vue-router';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { FeatureService } from '@shared/directives/Feature.service';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const router = useRouter();

    const state = reactive<{
        refreshingTranslations: boolean;
    }>({
        refreshingTranslations: false,
    });

    onMounted(async () => {
        if (!featureAccess.hasFeature('BackOffice')) {
            window.location.href = '/';
        }
    });

    async function refreshTranslationsClicked(): Promise<void> {
        state.refreshingTranslations = true;

        await refreshTranslations().then(() => {
            location.reload();
        });
    }

    const isCurrent = (routeNames: string[]) => {
        let currentPathObject = router.currentRoute.value;

        return routeNames.includes(currentPathObject.name?.toString() || '');
    };
</script>

<template>
    <nav class="navbar-default navbar main-navbar" role="navigation">
        <div class="container-fluid">
            <p class="navbar-text navbar-title m-auto-r">Back Office 2.0</p>

            <ul class="nav navbar-nav list-unstyled">
                <li>
                    <a v-if="!state.refreshingTranslations" href="#" @click="refreshTranslationsClicked">
                        {{ $Resources.RefreshTranslations }}
                    </a>
                </li>
            </ul>
        </div>
    </nav>

    <div class="has-sub-menu">
        <nav role="navigation" class="sidebar set-height custom-scrollbar navbar-sticky">
            <ul class="tabs list-unstyled display-row" role="tablist" data-tabs-container>
                <li>
                    <h4 class="tabs-heading">Start</h4>

                    <ul class="tabs list-unstyled display-row">
                        <li>
                            <a href="/BackOffice?tab=dashboard">Dashboard</a>
                        </li>
                        <li :class="{ active: isCurrent(['SubscriptionStats']) }">
                            <router-link :to="{ name: 'SubscriptionStats' }">Subscription Stats</router-link>
                        </li>
                    </ul>
                </li>
                <li>
                    <h4 class="tabs-heading">Accounts</h4>

                    <ul class="tabs list-unstyled display-row">
                        <li :class="{ active: isCurrent(['AccountList', 'AccountEdit']) }">
                            <router-link :to="{ name: 'AccountList' }">Accounts</router-link>
                        </li>
                        <li :class="{ active: isCurrent(['SubscriptionList', 'SubscriptionEdit']) }">
                            <router-link :to="{ name: 'SubscriptionList' }">Subscriptions</router-link>
                        </li>
                        <li :class="{ active: isCurrent(['UserList', 'UserEdit']) }">
                            <router-link :to="{ name: 'UserList' }">Users</router-link>
                        </li>
                        <li>
                            <a href="/BackOffice?tab=subscriptions-dashboard">Subscriptions dashboard</a>
                        </li>
                        <li>
                            <a href="/BackOffice?tab=billing">Billing</a>
                        </li>
                    </ul>
                </li>
                <li>
                    <h4 class="tabs-heading">System</h4>

                    <ul class="tabs list-unstyled display-row">
                        <li :class="{ active: isCurrent(['Features']) }">
                            <router-link :to="{ name: 'Features' }">Features</router-link>
                        </li>
                        <li :class="{ active: isCurrent(['FeatureSets']) }">
                            <router-link :to="{ name: 'FeatureSets' }">Feature sets</router-link>
                        </li>
                        <li>
                            <a href="/BackOffice?tab=featureFlags">Feature flags</a>
                        </li>
                        <li :class="{ active: isCurrent(['Themes']) }">
                            <router-link :to="{ name: 'Themes' }">Themes</router-link>
                        </li>
                        <li>
                            <a href="/BackOffice/Health">Health monitor</a>
                        </li>
                        <li>
                            <a href="/BackOffice?tab=miscellaneous">Miscellaneous</a>
                        </li>
                    </ul>
                </li>
                <li>
                    <h4 class="tabs-heading">Managing</h4>

                    <ul class="tabs list-unstyled display-row">
                        <li :class="{ active: isCurrent(['ManageUsers']) }">
                            <router-link :to="{ name: 'ManageUsers' }">Manage users</router-link>
                        </li>
                        <li :class="{ active: isCurrent(['TaskManager']) }">
                            <router-link :to="{ name: 'TaskManager' }">Manage tasks</router-link>
                        </li>
                        <li>
                            <a href="/BackOffice?tab=manageMessages">Manage messages</a>
                        </li>
                        <li :class="{ active: isCurrent(['ManageWorkers']) }">
                            <router-link :to="{ name: 'ManageWorkers' }">Manage workers</router-link>
                        </li>
                        <li :class="{ active: isCurrent(['OAuthProviders']) }">
                            <router-link :to="{ name: 'OAuthProviders' }">Manage OAuthProviders</router-link>
                        </li>
                        <li>
                            <a href="/BackOffice?tab=latestSms">Latest sent sms</a>
                        </li>
                        <li :class="{ active: isCurrent(['LatestIssues']) }">
                            <router-link :to="{ name: 'LatestIssues' }">Latest sent mailings</router-link>
                        </li>
                    </ul>
                </li>
                <li>
                    <h4 class="tabs-heading">Configuration</h4>

                    <ul class="tabs list-unstyled display-row">
                        <li>
                            <a href="/BackOffice?tab=pricing">Pricing</a>
                        </li>
                        <li>
                            <a href="/BackOffice?tab=globalTemplates">Templates</a>
                        </li>
                        <li :class="{ active: isCurrent(['SmtpServers']) }">
                            <router-link :to="{ name: 'SmtpServers' }">Smtp servers</router-link>
                        </li>

                        <li>
                            <a href="/BackOffice?tab=backOfficeAdministrators">Back Office Administrators</a>
                        </li>
                    </ul>
                </li>
            </ul>
        </nav>

        <section class="fadeIn animated">
            <router-view></router-view>
        </section>
    </div>
</template>

<style></style>
